/**
 * DocumentPaper — the faux scanned "Смета" (contractor quote) surface
 * (design revamp, handoff-2026-06).
 *
 * Renders the project's quote as a deliberately paper-like document: an
 * off-white paper field (`--g-paper`) with warm-black ink
 * (`--g-paper-ink`) in the monospace document face (`.font-mono-doc` ->
 * `--g-mono`, IBM Plex Mono). The header carries the quote number,
 * contractor · date, address, and phone above a dashed divider; the body
 * is a striped list of line rows (zebra-tinted), and the footer is the
 * "Подпись / Печать" sign-off above a second dashed divider.
 *
 * This surface is intentionally NOT theme-reactive in its paper/ink
 * colours — a scanned page looks the same in light and dark — so it uses
 * the fixed `--g-paper` / `--g-paper-ink` tokens (declared in theme.css)
 * rather than the neutral `--g-surface` / `--g-ink` chain. The accent
 * tokens used for the focused row DO follow the active palette.
 *
 * Focus behaviour: the row whose `material.id === focusId` gets an accent
 * left-border and a faint accent tint, and (when `onLineClick` is set)
 * every row is clickable, invoking `onLineClick(id)`.
 *
 * Props mirror the fixture shapes in
 * docs/GardEn/handoff-2026-06/garden/data.jsx:
 *   project.quote -> { no, contractor, date, addr, phone }
 *   material      -> { id, line, name: {ru,ky,en}, qty, unit: {ru,ky,en} }
 *
 * Visual reference: docs/GardEn/handoff-2026-06/garden/ui.jsx
 * `DocumentPaper` + docs/GardEn/handoff-2026-06/garden/screens-materials.jsx
 * usage.
 */
import type { ReactElement } from "react";

/** The three first-class GardEn locales (RU default, KY, EN). RU is the
 * fallback when a KY/EN value is missing. */
export type DocumentLang = "ru" | "ky" | "en";

/** A localised string with RU required (the fallback locale) and
 * KY/EN optional-but-expected at MVP. */
export interface LocalizedText {
  ru: string;
  ky?: string;
  en?: string;
}

/** The contractor quote header block, mirroring `project.quote`. */
export interface DocumentQuote {
  /** Quote number, rendered as «СМЕТА №{no}». */
  no: string;
  /** Contractor name (e.g. "ИП Шамшиев"). */
  contractor: string;
  /** Quote date (display string, e.g. "15.05.2026"). */
  date: string;
  /** Site address. */
  addr: string;
  /** Contractor phone (display string). */
  phone: string;
}

/** The project wrapper — only `quote` is consumed by this surface. */
export interface DocumentProject {
  quote: DocumentQuote;
}

/** A single quote line item, mirroring the material fixture shape. */
export interface DocumentMaterial {
  /** Stable id; matched against `focusId` and passed to `onLineClick`. */
  id: string;
  /** 1-indexed line number, zero-padded to two digits in the render. */
  line: number;
  /** Localised material name. */
  name: LocalizedText;
  /** Quantity (numeric, rendered with tabular figures). */
  qty: number;
  /** Localised unit (e.g. м² / packs). */
  unit: LocalizedText;
}

export interface DocumentPaperProps {
  /** The project whose `quote` header is rendered. */
  project: DocumentProject;
  /** The quote line items, in display order. */
  materials: DocumentMaterial[];
  /** Optional id of the focused (highlighted) line. */
  focusId?: string;
  /** Active locale; falls back to RU when the chosen locale is missing. */
  lang: DocumentLang;
  /** Optional click handler; presence makes every row interactive. */
  onLineClick?: (id: string) => void;
}

/** Pick the localised value, falling back to RU (the fallback locale). */
function pickLocale(text: LocalizedText, lang: DocumentLang): string {
  return text[lang] ?? text.ru;
}

/**
 * Renders the faux scanned quote document.
 *
 * Each line row is a `<button>` when `onLineClick` is supplied (focusable,
 * keyboard-activatable) and a passive `<div>` otherwise — mirroring the
 * polymorphic affordance pattern used elsewhere in the library (GPill).
 */
export function DocumentPaper({
  project,
  materials,
  focusId,
  lang,
  onLineClick,
}: DocumentPaperProps): ReactElement {
  const quote = project.quote;
  const interactive = Boolean(onLineClick);

  return (
    <div
      data-testid="garden-document-paper"
      className="overflow-hidden rounded-[var(--g-radius)] border border-[color-mix(in_srgb,var(--g-paper-ink)_10%,transparent)] bg-[var(--g-paper)] font-mono-doc text-[var(--g-paper-ink)] shadow-[var(--g-shadow-sm)]"
    >
      {/* Header — quote number + contractor meta, dashed divider below. */}
      <div className="border-b border-dashed border-[color-mix(in_srgb,var(--g-paper-ink)_18%,transparent)] px-[18px] py-4">
        <div className="text-[13px] font-bold tracking-[0.02em]">
          СМЕТА №{quote.no}
        </div>
        <div className="mt-1 text-[11px] leading-[1.5] text-[color-mix(in_srgb,var(--g-paper-ink)_70%,transparent)]">
          {quote.contractor} · {quote.date}
          <br />
          {quote.addr}
          <br />
          {quote.phone}
        </div>
      </div>

      {/* Body — zebra-striped line rows; focused row gets accent rail+tint. */}
      <div data-testid="garden-document-rows">
        {materials.map((material, index) => {
          const focused = material.id === focusId;
          const zebra = index % 2 === 1;
          const rowClasses = [
            "flex",
            "w-full",
            "items-baseline",
            "gap-2.5",
            "px-[18px]",
            "py-[9px]",
            "text-left",
            "text-[11.5px]",
            "transition-colors",
            "border-l-[3px]",
            focused
              ? "border-accent bg-[color-mix(in_srgb,var(--g-accent)_22%,var(--g-paper))]"
              : zebra
                ? "border-transparent bg-[color-mix(in_srgb,var(--g-paper-ink)_2.5%,transparent)]"
                : "border-transparent bg-transparent",
            interactive ? "cursor-pointer" : "",
          ]
            .filter(Boolean)
            .join(" ");

          const rowContent = (
            <>
              <span className="w-[18px] shrink-0 text-[color-mix(in_srgb,var(--g-paper-ink)_50%,transparent)]">
                {String(material.line).padStart(2, "0")}
              </span>
              <span className="flex-1 leading-[1.4]">
                {pickLocale(material.name, lang)}
              </span>
              <span className="shrink-0 tabular-nums text-[color-mix(in_srgb,var(--g-paper-ink)_75%,transparent)]">
                {material.qty} {pickLocale(material.unit, lang)}
              </span>
            </>
          );

          if (interactive) {
            return (
              <button
                key={material.id}
                type="button"
                data-testid="garden-document-row"
                data-line-id={material.id}
                data-focused={focused ? "true" : "false"}
                onClick={() => onLineClick?.(material.id)}
                className={rowClasses}
              >
                {rowContent}
              </button>
            );
          }

          return (
            <div
              key={material.id}
              data-testid="garden-document-row"
              data-line-id={material.id}
              data-focused={focused ? "true" : "false"}
              className={rowClasses}
            >
              {rowContent}
            </div>
          );
        })}
      </div>

      {/* Footer — sign-off line above a dashed divider. */}
      <div className="border-t border-dashed border-[color-mix(in_srgb,var(--g-paper-ink)_18%,transparent)] px-[18px] py-3 text-[10.5px] text-[color-mix(in_srgb,var(--g-paper-ink)_55%,transparent)]">
        Подпись: ____________ Печать
      </div>
    </div>
  );
}
